import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  EyeIcon,
  EditIcon,
  MapPinIcon,
  UserIcon,
  UsersIcon,
  Loader2Icon,
  ClockIcon,
  PlayIcon,
  PauseIcon,
  CheckCircleIcon,
  AlertTriangleIcon,
  NavigationIcon,
  BarChart3Icon,
  CalendarIcon,
} from 'lucide-react'
import { supabase } from '../lib/supabase'
import { fetchCrewsAndEmployees } from '../api/jobWorkflow'
import { formatEmployeeLabel } from '../lib/formatEmployeeLabel'
import { softUnassign, addAssignment, jobHasAssigneeCoverage } from '../lib/jobAssignmentHistory'
import { DayJobAssignPanel } from '../components/job/DayJobAssignPanel'
import { DispatchBoard } from '../components/job/DispatchBoard'
import { googleMapsDirectionsUrl, optimizeRouteOrder, type LatLng } from '../lib/routeOptimizer'
import {
  assignJobsToClosestEmployees,
  fetchEmployeeLastLocations,
} from '../lib/employeeLastLocation'
import { geocodeMissingProperties } from '../lib/geocodeProperty'
import { evaluateJobCompletionGate } from '../lib/jobCompletionGate'
import { isFieldCompleteStatus, resolvePostCompletionStatus } from '../lib/jobStatusWorkflow'
import { useAppSettings } from '../hooks/useAppSettings'
import { usePermissions } from '../hooks/usePermissions'
import { formatDateInputValue } from '../lib/dateUtils'
import { useDivision } from '../contexts/DivisionContext'
import { CompletionExceptionModal } from '../components/job/CompletionExceptionModal'
import {
  canCompleteJobWithException,
  completionExceptionUpdate,
} from '../lib/jobCompletionException'
import {
  mobile,
  MobilePage,
  MobilePageHeader,
  MobileListRow,
  MobileRowAction,
  MobileEmptyState,
  MobileStatStrip,
  MobileSelect,
} from '../components/mobile'

interface DayJob {
  id: string
  job_number: string
  title: string
  scheduled_date: string
  status: string
  priority: string
  service_type?: string
  operational_division_id?: string | null
  description?: string
  estimated_duration?: number
  assigned_crew_id?: string | null
  assigned_employee_id?: string | null
  customer?: {
    company_name?: string
    contact_first_name?: string
    contact_last_name?: string
    phone?: string
  }
  property_id?: string | null
  property?: {
    id?: string
    name?: string
    address?: string
    city?: string
    state?: string
    zip_code?: string | null
    latitude?: number | null
    longitude?: number | null
  }
  contract_id?: string
  is_recurring?: boolean
  scheduled_start_time?: string
  scheduled_end_time?: string
  route_order?: number | null
}

interface Crew {
  id: string
  name: string
  is_active: boolean
  is_subcontractor?: boolean | null
}

interface Employee {
  id: string
  first_name: string
  last_name: string
  employee_number?: string | null
  role?: string
  is_active?: boolean
}

interface JobAssignment {
  id: string
  job_id: string
  crew_id?: string | null
  employee_id?: string | null
  crew?: Crew | null
  employee?: Employee | null
}

type ViewMode = 'schedule' | 'dispatch' | 'progress' | 'routing' | 'analytics'

type RouteBucket = {
  key: string
  label: string
  kind: 'employee' | 'crew' | 'unassigned'
  employeeId?: string
  crewId?: string
  jobs: DayJob[]
}

export function DaySchedule() {
  const navigate = useNavigate()
  const { date } = useParams<{ date: string }>()

  const currentDate = date || new Date().toISOString().split('T')[0]

  const [jobs, setJobs] = useState<DayJob[]>([])
  const [loading, setLoading] = useState(true)
  const [viewMode, setViewMode] = useState<ViewMode>('schedule')
  const [statusFilter, setStatusFilter] = useState('all')
  const [updatingJobId, setUpdatingJobId] = useState<string | null>(null)

  // Crew/employee assignment state
  const [crews, setCrews] = useState<Crew[]>([])
  const [employees, setEmployees] = useState<Employee[]>([])
  const [jobAssignments, setJobAssignments] = useState<Record<string, JobAssignment[]>>({})
  const [assigningJobId, setAssigningJobId] = useState<string | null>(null)
  const [savingAssignment, setSavingAssignment] = useState(false)
  const [optimizingRoute, setOptimizingRoute] = useState(false)
  const [optimizingBucketKey, setOptimizingBucketKey] = useState<string | null>(null)
  const [mapsUrlByBucket, setMapsUrlByBucket] = useState<Record<string, string>>({})
  const [autoAssigning, setAutoAssigning] = useState(false)
  const { photoPolicy } = useAppSettings()
  const { profile } = usePermissions()
  const canExceptionComplete = canCompleteJobWithException(profile?.role)
  const [exceptionJobId, setExceptionJobId] = useState<string | null>(null)
  const [exceptionGateMessage, setExceptionGateMessage] = useState<string | null>(null)
  const { divisions, currentDivision, setCurrentDivision } = useDivision()
  const [dispatchScope, setDispatchScope] = useState<'division' | 'subs'>('division')

  const dateFormatted = new Date(currentDate + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true)

      // Prefer geocode columns when present; fall back if migration not applied yet.
      // Do not ORDER BY route_order in SQL — missing column would empty the whole day.
      let { data, error } = await supabase
        .from('jobs')
        .select(`
          *,
          customer:customers(company_name, contact_first_name, contact_last_name, phone),
          property:properties(id, name, address, city, state, zip_code, latitude, longitude)
        `)
        .eq('scheduled_date', currentDate)
        .order('scheduled_start_time', { ascending: true })

      if (error) {
        const fallback = await supabase
          .from('jobs')
          .select(`
            *,
            customer:customers(company_name, contact_first_name, contact_last_name, phone),
            property:properties(id, name, address, city, state, zip_code)
          `)
          .eq('scheduled_date', currentDate)
          .order('scheduled_start_time', { ascending: true })
        data = fallback.data
        error = fallback.error
      }

      if (error) throw error

      const mapped = (data || []).map((row: any) => ({
        ...row,
        customer: Array.isArray(row.customer) ? row.customer[0] || null : row.customer,
        property: Array.isArray(row.property) ? row.property[0] || null : row.property,
      })) as DayJob[]

      mapped.sort((a, b) => {
        const ao = a.route_order ?? Number.MAX_SAFE_INTEGER
        const bo = b.route_order ?? Number.MAX_SAFE_INTEGER
        if (ao !== bo) return ao - bo
        return (a.scheduled_start_time || '').localeCompare(b.scheduled_start_time || '')
      })

      setJobs(mapped)

      // Backfill missing lat/lng in the background (don't block the day list).
      const needsGeocode = mapped.some(
        (j) =>
          j.property &&
          (j.property.latitude == null || j.property.longitude == null) &&
          j.property.address?.trim()
      )
      if (needsGeocode) {
        void geocodeMissingProperties(
          mapped
            .map((j) => {
              const id = j.property?.id || j.property_id
              if (!id || !j.property) return null
              return { id, ...j.property }
            })
            .filter(Boolean) as Array<{
            id: string
            address?: string | null
            city?: string | null
            state?: string | null
            zip_code?: string | null
            latitude?: number | null
            longitude?: number | null
          }>
        ).then((coordsByProp) => {
          if (coordsByProp.size === 0) return
          setJobs((prev) =>
            prev.map((j) => {
              const pid = j.property?.id || j.property_id
              const coords = pid ? coordsByProp.get(pid) : undefined
              if (!coords || !j.property) return j
              return {
                ...j,
                property: {
                  ...j.property,
                  latitude: coords.latitude,
                  longitude: coords.longitude,
                },
              }
            })
          )
        })
      }
      return mapped
    } catch (err) {
      console.error('Error fetching day jobs:', err)
      setJobs([])
      return []
    } finally {
      setLoading(false)
    }
  }, [currentDate])

  const loadCrewsAndEmployees = useCallback(async () => {
    try {
      const { crews: c, employees: e } = await fetchCrewsAndEmployees()
      setCrews(c.map((row) => ({ ...row, is_active: true })))
      setEmployees(e.map((row) => ({ ...row, is_active: true })))
    } catch (err) {
      console.error('Error fetching crews/employees:', err)
    }
  }, [])

  const fetchAssignments = useCallback(async (dayJobs: DayJob[]) => {
    const jobIds = dayJobs.map((j) => j.id)
    if (jobIds.length === 0) {
      setJobAssignments({})
      return
    }
    try {
      const loadGrouped = async (): Promise<Record<string, JobAssignment[]>> => {
        const { data, error } = await supabase
          .from('job_assignments')
          .select(`
            id, job_id, crew_id, employee_id,
            crew:crews(id, name, is_active, is_subcontractor),
            employee:employees(id, first_name, last_name, employee_number, role)
          `)
          .in('job_id', jobIds)
          .is('unassigned_at', null)

        if (error) throw error
        const grouped: Record<string, JobAssignment[]> = {}
        for (const row of data || []) {
          const a: JobAssignment = {
            ...row,
            crew: Array.isArray(row.crew) ? row.crew[0] || null : row.crew,
            employee: Array.isArray(row.employee) ? row.employee[0] || null : row.employee,
          }
          if (!grouped[a.job_id]) grouped[a.job_id] = []
          grouped[a.job_id].push(a)
        }
        return grouped
      }

      let grouped = await loadGrouped()

      // Heal: property-default reapply can set jobs.assigned_* without job_assignments rows
      let healed = false
      for (const job of dayJobs) {
        const active = grouped[job.id] || []
        if (
          job.assigned_employee_id &&
          !active.some((a) => a.employee_id === job.assigned_employee_id)
        ) {
          try {
            const { added } = await addAssignment({
              jobId: job.id,
              employeeId: job.assigned_employee_id,
            })
            if (added) healed = true
          } catch (err) {
            console.warn('Failed to sync employee assignment for job', job.id, err)
          }
        }
        if (
          job.assigned_crew_id &&
          !active.some((a) => a.crew_id === job.assigned_crew_id)
        ) {
          try {
            const { added } = await addAssignment({
              jobId: job.id,
              crewId: job.assigned_crew_id,
            })
            if (added) healed = true
          } catch (err) {
            console.warn('Failed to sync crew assignment for job', job.id, err)
          }
        }
      }

      if (healed) {
        grouped = await loadGrouped()
      }
      setJobAssignments(grouped)
    } catch (err) {
      console.error('Error fetching job assignments:', err)
    }
  }, [])

  useEffect(() => { fetchJobs() }, [fetchJobs])
  useEffect(() => { loadCrewsAndEmployees() }, [loadCrewsAndEmployees])
  useEffect(() => {
    void fetchAssignments(jobs)
  }, [jobs, fetchAssignments])

  const handleAssignCrew = async (jobId: string, crewId: string) => {
    try {
      setSavingAssignment(true)
      await addAssignment({ jobId, crewId: crewId || null })
      await fetchAssignments(jobs)
      await fetchJobs()
    } catch (err) {
      console.error('Error assigning crew:', err)
    } finally {
      setSavingAssignment(false)
    }
  }

  const handleAssignEmployee = async (jobId: string, employeeId: string) => {
    try {
      setSavingAssignment(true)
      await addAssignment({ jobId, employeeId: employeeId || null })
      await fetchAssignments(jobs)
      await fetchJobs()
    } catch (err) {
      console.error('Error assigning employee:', err)
    } finally {
      setSavingAssignment(false)
    }
  }

  const handleRemoveAssignment = async (assignmentId: string, jobId: string) => {
    try {
      setSavingAssignment(true)
      const assignment = jobAssignments[jobId]?.find((a) => a.id === assignmentId)
      await softUnassign({
        assignmentId,
        jobId,
        crewId: assignment?.crew_id,
        employeeId: assignment?.employee_id,
      })
      const freshJobs = await fetchJobs()
      await fetchAssignments(freshJobs)
    } catch (err) {
      console.error('Error removing assignment:', err)
    } finally {
      setSavingAssignment(false)
    }
  }

  const getPrimaryAssignee = useCallback(
    (job: DayJob): { employeeId?: string; crewId?: string; label: string } => {
      const rows = jobAssignments[job.id] || []
      const empRow = rows.find((r) => r.employee_id)
      const crewRow = rows.find((r) => r.crew_id)
      const employeeId = job.assigned_employee_id || empRow?.employee_id || undefined
      const crewId = job.assigned_crew_id || crewRow?.crew_id || undefined
      if (employeeId) {
        const emp =
          employees.find((e) => e.id === employeeId) ||
          empRow?.employee ||
          null
        const label = emp ? formatEmployeeLabel(emp) : 'Employee'
        return { employeeId, label }
      }
      if (crewId) {
        const crew = crews.find((c) => c.id === crewId) || crewRow?.crew || null
        return { crewId, label: crew?.name || 'Crew' }
      }
      return { label: 'Unassigned' }
    },
    [crews, employees, jobAssignments]
  )

  const applyJobStatusChange = async (
    jobId: string,
    newStatus: string,
    exception?: { reason: string }
  ) => {
    if (!newStatus?.trim()) return
    try {
      setUpdatingJobId(jobId)
      const persistedStatus = resolvePostCompletionStatus(newStatus)
      if (!persistedStatus) return
      const updateData: any = { status: persistedStatus }
      if (newStatus === 'in_progress') updateData.actual_start_time = new Date().toISOString()
      else if (newStatus === 'completed') {
        updateData.actual_end_time = new Date().toISOString()
        if (exception?.reason?.trim()) {
          const { data: auth } = await supabase.auth.getUser()
          Object.assign(updateData, completionExceptionUpdate(exception.reason, auth.user?.id))
        }
      }

      const { error } = await supabase.from('jobs').update(updateData).eq('id', jobId)
      if (error) throw error
      await fetchJobs()
    } catch (error) {
      console.error('Error updating job status:', error)
      const e = error as { message?: string; details?: string; hint?: string } | null
      const msg = [e?.message, e?.details, e?.hint].filter(Boolean).join(' — ')
      alert(msg ? `Failed to update job status: ${msg}` : 'Failed to update job status')
    } finally {
      setUpdatingJobId(null)
    }
  }

  const handleJobStatusChange = async (jobId: string, newStatus: string) => {
    if (!newStatus?.trim()) return
    if (newStatus === 'completed') {
      const gate = await evaluateJobCompletionGate(jobId, photoPolicy)
      if (!gate.ok) {
        if (canExceptionComplete) {
          setExceptionGateMessage(gate.message || 'Checklist or photos incomplete')
          setExceptionJobId(jobId)
          return
        }
        alert(gate.message)
        navigate(`/jobs/details/${jobId}`)
        return
      }
    }

    await applyJobStatusChange(jobId, newStatus)
  }

  const subcontractorCrewIds = useMemo(
    () => new Set(crews.filter((crew) => crew.is_subcontractor).map((crew) => crew.id)),
    [crews]
  )

  const isSubcontractorJob = useCallback(
    (job: DayJob) =>
      (!!job.assigned_crew_id && subcontractorCrewIds.has(job.assigned_crew_id)) ||
      (jobAssignments[job.id] || []).some(
        (assignment) =>
          !!assignment.crew_id && subcontractorCrewIds.has(assignment.crew_id)
      ),
    [jobAssignments, subcontractorCrewIds]
  )

  const matchesDivision = useCallback(
    (job: DayJob, division: (typeof divisions)[number]) =>
      job.operational_division_id === division.id ||
      (!job.operational_division_id &&
        !!job.service_type &&
        (division.service_types || []).includes(job.service_type)),
    []
  )

  const scopeJobs = useMemo(() => {
    if (dispatchScope === 'subs') return jobs.filter(isSubcontractorJob)
    if (!currentDivision) return jobs
    return jobs.filter((job) => matchesDivision(job, currentDivision))
  }, [currentDivision, dispatchScope, isSubcontractorJob, jobs, matchesDivision])

  const filteredJobs = scopeJobs.filter(
    (job) => statusFilter === 'all' || job.status === statusFilter
  )

  const navigateDay = (offset: number) => {
    const d = new Date(currentDate + 'T00:00:00')
    d.setDate(d.getDate() + offset)
    navigate(`/dispatch/${formatDateInputValue(d)}`)
  }

  // Helpers
  const getCustomerName = (job: DayJob) =>
    job.customer?.company_name ||
    `${job.customer?.contact_first_name || ''} ${job.customer?.contact_last_name || ''}`.trim() ||
    'N/A'

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled': return 'bg-blue-100 text-blue-800'
      case 'in_progress': return 'bg-yellow-100 text-yellow-800'
      case 'completed': return 'bg-green-100 text-green-800'
      case 'cancelled': return 'bg-red-100 text-red-800'
      case 'pending_review': return 'bg-purple-100 text-purple-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800'
      case 'medium': return 'bg-yellow-100 text-yellow-800'
      case 'low': return 'bg-green-100 text-green-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const formatTime = (time: string) => {
    if (!time) return 'N/A'
    return new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'scheduled': return <ClockIcon className="w-4 h-4" />
      case 'in_progress': return <PlayIcon className="w-4 h-4" />
      case 'completed': return <CheckCircleIcon className="w-4 h-4" />
      case 'cancelled': return <AlertTriangleIcon className="w-4 h-4" />
      case 'pending_review': return <PauseIcon className="w-4 h-4" />
      default: return <ClockIcon className="w-4 h-4" />
    }
  }

  // Day-level stats
  const totalJobs = scopeJobs.length
  const completedJobs = scopeJobs.filter((j) => isFieldCompleteStatus(j.status)).length
  const inProgressJobs = scopeJobs.filter((j) => j.status === 'in_progress').length
  const scheduledJobs = scopeJobs.filter((j) => j.status === 'scheduled').length
  const completionRate = totalJobs > 0 ? Math.round((completedJobs / totalJobs) * 100) : 0
  const unassignedCount = scopeJobs.filter(
    (j) => !jobHasAssigneeCoverage(j, jobAssignments[j.id])
  ).length
  const countUnassigned = (candidateJobs: DayJob[]) =>
    candidateJobs.filter((job) => !jobHasAssigneeCoverage(job, jobAssignments[job.id])).length
  const subcontractorJobs = jobs.filter(isSubcontractorJob)

  const { crewJobCounts, employeeJobCounts } = useMemo(() => {
    const crewCounts: Record<string, number> = {}
    const empCounts: Record<string, number> = {}
    Object.values(jobAssignments).forEach((list) => {
      list.forEach((a) => {
        if (a.crew_id) crewCounts[a.crew_id] = (crewCounts[a.crew_id] || 0) + 1
        if (a.employee_id) empCounts[a.employee_id] = (empCounts[a.employee_id] || 0) + 1
      })
    })
    return { crewJobCounts: crewCounts, employeeJobCounts: empCounts }
  }, [jobAssignments])

  const routeBuckets = useMemo((): RouteBucket[] => {
    const map = new Map<string, RouteBucket>()
    for (const job of filteredJobs) {
      const primary = getPrimaryAssignee(job)
      const key = primary.employeeId
        ? `employee:${primary.employeeId}`
        : primary.crewId
          ? `crew:${primary.crewId}`
          : 'unassigned'
      const kind: RouteBucket['kind'] = primary.employeeId
        ? 'employee'
        : primary.crewId
          ? 'crew'
          : 'unassigned'
      if (!map.has(key)) {
        map.set(key, {
          key,
          label: primary.label,
          kind,
          employeeId: primary.employeeId,
          crewId: primary.crewId,
          jobs: [],
        })
      }
      map.get(key)!.jobs.push(job)
    }
    const buckets = Array.from(map.values())
    for (const bucket of buckets) {
      bucket.jobs.sort((a: DayJob, b: DayJob) => {
        const ao = a.route_order ?? Number.MAX_SAFE_INTEGER
        const bo = b.route_order ?? Number.MAX_SAFE_INTEGER
        if (ao !== bo) return ao - bo
        return (a.scheduled_start_time || '').localeCompare(b.scheduled_start_time || '')
      })
    }
    return buckets.sort((a: RouteBucket, b: RouteBucket) => {
      if (a.kind === 'unassigned' && b.kind !== 'unassigned') return 1
      if (b.kind === 'unassigned' && a.kind !== 'unassigned') return -1
      return a.label.localeCompare(b.label)
    })
  }, [filteredJobs, getPrimaryAssignee])

  const buildStopsForJobs = async (bucketJobs: DayJob[]) => {
    const coordsByProp = await geocodeMissingProperties(
      bucketJobs
        .map((j) => {
          const id = j.property?.id || j.property_id
          if (!id || !j.property) return null
          return { id, ...j.property }
        })
        .filter(Boolean) as Array<{
        id: string
        address?: string | null
        city?: string | null
        state?: string | null
        zip_code?: string | null
        latitude?: number | null
        longitude?: number | null
      }>
    )
    const stops = bucketJobs.map((j) => {
      const pid = j.property?.id || j.property_id
      const coords = pid ? coordsByProp.get(pid) : undefined
      return {
        id: j.id,
        latitude: coords?.latitude ?? j.property?.latitude,
        longitude: coords?.longitude ?? j.property?.longitude,
      }
    })
    return { stops, coordsByProp }
  }

  const persistRouteOrder = async (orderedIds: string[]) => {
    await Promise.all(
      orderedIds.map((id, index) =>
        supabase.from('jobs').update({ route_order: index + 1 }).eq('id', id)
      )
    )
  }

  const handleOptimizeBucket = async (bucket: RouteBucket) => {
    if (bucket.jobs.length < 2) return
    try {
      setOptimizingRoute(true)
      setOptimizingBucketKey(bucket.key)
      const { stops, coordsByProp } = await buildStopsForJobs(bucket.jobs)

      let start: LatLng | null = null
      if (bucket.employeeId) {
        const locs = await fetchEmployeeLastLocations([bucket.employeeId])
        const loc = locs.get(bucket.employeeId)
        if (loc) start = { latitude: loc.latitude, longitude: loc.longitude }
      }

      const orderedIds = optimizeRouteOrder(stops, start)
      await persistRouteOrder(orderedIds)

      const orderedJobs = orderedIds
        .map((id) => bucket.jobs.find((j) => j.id === id))
        .filter(Boolean) as DayJob[]
      const coords = orderedJobs
        .map((j) => {
          const pid = j.property?.id || j.property_id
          const fromMap = pid ? coordsByProp.get(pid) : undefined
          const latitude = fromMap?.latitude ?? j.property?.latitude
          const longitude = fromMap?.longitude ?? j.property?.longitude
          return latitude != null && longitude != null
            ? { latitude, longitude }
            : null
        })
        .filter(Boolean) as Array<{ latitude: number; longitude: number }>
      const url = googleMapsDirectionsUrl(coords)
      if (url) {
        setMapsUrlByBucket((prev) => ({ ...prev, [bucket.key]: url }))
      }
      await fetchJobs()
    } catch (err) {
      console.error('Error optimizing route bucket:', err)
    } finally {
      setOptimizingRoute(false)
      setOptimizingBucketKey(null)
    }
  }

  const handleOptimizeAllRoutes = async () => {
    const targets = routeBuckets.filter(
      (b) => b.kind !== 'unassigned' && b.jobs.length >= 2
    )
    if (targets.length === 0) return
    try {
      setOptimizingRoute(true)
      const empIds = targets
        .map((b) => b.employeeId)
        .filter(Boolean) as string[]
      const locs = await fetchEmployeeLastLocations(empIds)
      const nextMaps: Record<string, string> = {}

      for (const bucket of targets) {
        setOptimizingBucketKey(bucket.key)
        const { stops, coordsByProp } = await buildStopsForJobs(bucket.jobs)
        const loc = bucket.employeeId ? locs.get(bucket.employeeId) : null
        const start = loc
          ? { latitude: loc.latitude, longitude: loc.longitude }
          : null
        const orderedIds = optimizeRouteOrder(stops, start)
        await persistRouteOrder(orderedIds)
        const orderedJobs = orderedIds
          .map((id) => bucket.jobs.find((j) => j.id === id))
          .filter(Boolean) as DayJob[]
        const coords = orderedJobs
          .map((j) => {
            const pid = j.property?.id || j.property_id
            const fromMap = pid ? coordsByProp.get(pid) : undefined
            const latitude = fromMap?.latitude ?? j.property?.latitude
            const longitude = fromMap?.longitude ?? j.property?.longitude
            return latitude != null && longitude != null
              ? { latitude, longitude }
              : null
          })
          .filter(Boolean) as Array<{ latitude: number; longitude: number }>
        const url = googleMapsDirectionsUrl(coords)
        if (url) nextMaps[bucket.key] = url
      }
      setMapsUrlByBucket((prev) => ({ ...prev, ...nextMaps }))
      await fetchJobs()
    } catch (err) {
      console.error('Error optimizing all routes:', err)
    } finally {
      setOptimizingRoute(false)
      setOptimizingBucketKey(null)
    }
  }

  const handleAutoAssignByProximity = async () => {
    const unassigned = filteredJobs.filter(
      (j) => !jobHasAssigneeCoverage(j, jobAssignments[j.id])
    )
    if (unassigned.length === 0 || employees.length === 0) return
    try {
      setAutoAssigning(true)
      await geocodeMissingProperties(
        unassigned
          .map((j) => {
            const id = j.property?.id || j.property_id
            if (!id || !j.property) return null
            return { id, ...j.property }
          })
          .filter(Boolean) as Array<{
          id: string
          address?: string | null
          city?: string | null
          state?: string | null
          zip_code?: string | null
          latitude?: number | null
          longitude?: number | null
        }>
      )
      const fresh = await fetchJobs()
      const dayJobs = (fresh || jobs).filter((j) =>
        unassigned.some((u) => u.id === j.id)
      )
      const locs = await fetchEmployeeLastLocations(employees.map((e) => e.id))
      const matches = assignJobsToClosestEmployees({
        jobs: dayJobs
          .slice()
          .sort((a, b) =>
            (a.scheduled_start_time || '').localeCompare(b.scheduled_start_time || '')
          )
          .map((j) => ({
            id: j.id,
            latitude: j.property?.latitude,
            longitude: j.property?.longitude,
          })),
        employees: employees.map((e) => {
          const loc = locs.get(e.id)
          return {
            id: e.id,
            location: loc
              ? { latitude: loc.latitude, longitude: loc.longitude }
              : null,
            currentLoad: employeeJobCounts[e.id] || 0,
          }
        }),
      })
      for (const m of matches) {
        await addAssignment({ jobId: m.jobId, employeeId: m.employeeId })
      }
      const after = await fetchJobs()
      await fetchAssignments(after || jobs)
      if (matches.length === 0) {
        alert(
          'No proximity matches. Employees need a recent punch GPS or a completed job with a mapped property, and unassigned jobs need property coordinates.'
        )
      }
    } catch (err) {
      console.error('Error auto-assigning by proximity:', err)
      alert('Auto-assign failed. Check the console for details.')
    } finally {
      setAutoAssigning(false)
    }
  }

  // ========== VIEW RENDERERS ==========

  const renderAssignPanel = (jobId: string) => {
    const assignments = jobAssignments[jobId] || []
    const isAssigning = assigningJobId === jobId
    return (
      <DayJobAssignPanel
        assignments={assignments}
        crews={crews}
        employees={employees}
        isEditing={isAssigning}
        saving={savingAssignment}
        crewJobCounts={crewJobCounts}
        employeeJobCounts={employeeJobCounts}
        onToggleEdit={() => setAssigningJobId(isAssigning ? null : jobId)}
        onAssignCrew={(crewId) => handleAssignCrew(jobId, crewId)}
        onAssignEmployee={(employeeId) => handleAssignEmployee(jobId, employeeId)}
        onRemove={(assignmentId) => handleRemoveAssignment(assignmentId, jobId)}
      />
    )
  }

  const renderScheduleView = () => (
    <>
      {/* Mobile / PWA list */}
      <div className="native-mobile-list space-y-2">
        {filteredJobs.map((job) => {
          const isUpdating = updatingJobId === job.id
          return (
            <MobileListRow
              key={job.id}
              chevron={false}
              title={job.title}
              subtitle={`#${job.job_number}`}
              badges={
                <>
                  <span className={`${mobile.badge} inline-flex items-center gap-1 ${getStatusColor(job.status)}`}>
                    {getStatusIcon(job.status)}
                    {job.status.replace('_', ' ')}
                  </span>
                  <span className={`${mobile.badge} ${getPriorityColor(job.priority)}`}>
                    {job.priority}
                  </span>
                </>
              }
              meta={
                <>
                  <div className="flex items-center gap-1.5">
                    <ClockIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    {formatTime(job.scheduled_start_time || '')} – {formatTime(job.scheduled_end_time || '')}
                  </div>
                  <div className="flex items-center gap-1.5 truncate">
                    <UserIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    <span className="truncate">{getCustomerName(job)}</span>
                  </div>
                  <div className="flex items-center gap-1.5 truncate">
                    <MapPinIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    <span className="truncate">{job.property?.name || 'N/A'}</span>
                  </div>
                  <div className="pt-1">{renderAssignPanel(job.id)}</div>
                </>
              }
              footer={
                <>
                  {(job.status === 'draft' || job.status === 'scheduled') && (
                    <MobileRowAction
                      tone="success"
                      onClick={() => handleJobStatusChange(job.id, 'in_progress')}
                      className={isUpdating ? 'opacity-50 pointer-events-none' : ''}
                    >
                      <PlayIcon className="w-4 h-4" />
                      {isUpdating ? '…' : 'Start'}
                    </MobileRowAction>
                  )}
                  {job.status === 'in_progress' && (
                    <MobileRowAction
                      tone="success"
                      onClick={() => handleJobStatusChange(job.id, 'completed')}
                      className={isUpdating ? 'opacity-50 pointer-events-none' : ''}
                    >
                      <CheckCircleIcon className="w-4 h-4" />
                      {isUpdating ? '…' : 'Complete'}
                    </MobileRowAction>
                  )}
                  <MobileRowAction tone="primary" onClick={() => navigate(`/jobs/details/${job.id}`)}>
                    <EyeIcon className="w-4 h-4" />
                    View
                  </MobileRowAction>
                  <MobileRowAction onClick={() => navigate(`/jobs/edit/${job.id}`)}>
                    <EditIcon className="w-4 h-4" />
                    Edit
                  </MobileRowAction>
                </>
              }
            />
          )
        })}
      </div>

      {/* Desktop card grid */}
      <div className="native-desktop-table">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredJobs.map((job) => {
            return (
              <div
                key={job.id}
                className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/jobs/details/${job.id}`)}
              >
                <div className="flex items-start justify-between mb-2">
                  <span
                    className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(job.status)}`}
                  >
                    {getStatusIcon(job.status)}
                    {job.status.replace('_', ' ')}
                  </span>
                  <span
                    className={`inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded ${getPriorityColor(job.priority)}`}
                  >
                    {job.priority}
                  </span>
                </div>
                <h3 className="text-sm font-semibold text-gray-900 mb-0.5">{job.title}</h3>
                <p className="text-xs text-gray-400 mb-2">#{job.job_number}</p>
                <div className="space-y-1 text-xs text-gray-600 mb-3">
                  <div className="flex items-center gap-1.5">
                    <ClockIcon className="w-3.5 h-3.5 text-gray-400" />
                    {formatTime(job.scheduled_start_time || '')} - {formatTime(job.scheduled_end_time || '')}
                  </div>
                  <div className="flex items-center gap-1.5 truncate">
                    <UserIcon className="w-3.5 h-3.5 text-gray-400" />
                    {getCustomerName(job)}
                  </div>
                  <div className="flex items-center gap-1.5 truncate">
                    <MapPinIcon className="w-3.5 h-3.5 text-gray-400" />
                    {job.property?.name || 'N/A'}
                  </div>
                </div>

                <div onClick={(e) => e.stopPropagation()}>{renderAssignPanel(job.id)}</div>

                <div className="flex items-center justify-between pt-2 border-t border-gray-100 mt-3">
                  <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                    {(job.status === 'draft' || job.status === 'scheduled') && (
                      <button
                        onClick={() => handleJobStatusChange(job.id, 'in_progress')}
                        disabled={updatingJobId === job.id}
                        className="px-2.5 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition-colors disabled:opacity-50"
                      >
                        {updatingJobId === job.id ? '...' : 'Start'}
                      </button>
                    )}
                    {job.status === 'in_progress' && (
                      <button
                        onClick={() => handleJobStatusChange(job.id, 'completed')}
                        disabled={updatingJobId === job.id}
                        className="px-2.5 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition-colors disabled:opacity-50"
                      >
                        {updatingJobId === job.id ? '...' : 'Complete'}
                      </button>
                    )}
                  </div>
                  <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => navigate(`/jobs/details/${job.id}`)}
                      className="p-1.5 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-md transition-colors"
                      title="View Details"
                    >
                      <EyeIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => navigate(`/jobs/edit/${job.id}`)}
                      className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                      title="Edit"
                    >
                      <EditIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </>
  )

  const renderDispatchView = () => (
    <DispatchBoard
      jobs={filteredJobs.map((job) => ({
        id: job.id,
        job_number: job.job_number,
        title: job.title,
        status: job.status,
        scheduled_start_time: job.scheduled_start_time,
        scheduled_end_time: job.scheduled_end_time,
        customerLabel: getCustomerName(job),
        propertyLabel: job.property?.name || job.property?.address || 'N/A',
        assigned_crew_id: job.assigned_crew_id,
        assigned_employee_id: job.assigned_employee_id,
      }))}
      jobAssignments={jobAssignments}
      crews={crews}
      employees={employees}
      crewJobCounts={crewJobCounts}
      employeeJobCounts={employeeJobCounts}
      assigningJobId={assigningJobId}
      savingAssignment={savingAssignment}
      getStatusColor={getStatusColor}
      formatTime={formatTime}
      onToggleEdit={(jobId) => setAssigningJobId(assigningJobId === jobId ? null : jobId)}
      onAssignCrew={handleAssignCrew}
      onAssignEmployee={handleAssignEmployee}
      onRemove={handleRemoveAssignment}
      onViewJob={(jobId) => navigate(`/jobs/details/${jobId}`)}
      onAutoAssignByProximity={handleAutoAssignByProximity}
      autoAssigning={autoAssigning}
    />
  )

  const renderProgressView = () => (
    <>
      <div className="native-mobile-list space-y-2">
        {filteredJobs.map((job) => {
          const isUpdating = updatingJobId === job.id
          return (
            <MobileListRow
              key={job.id}
              chevron={false}
              leading={<div className="p-2 bg-blue-50 rounded-lg">{getStatusIcon(job.status)}</div>}
              title={job.title}
              subtitle={`#${job.job_number}`}
              badges={
                <>
                  <span className={`${mobile.badge} ${getStatusColor(job.status)}`}>
                    {job.status.replace('_', ' ')}
                  </span>
                  <span className={`${mobile.badge} ${getPriorityColor(job.priority)}`}>
                    {job.priority}
                  </span>
                </>
              }
              meta={
                <div className="flex flex-col gap-1">
                  <span className="flex items-center gap-1.5">
                    <UserIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    <span className="truncate">{getCustomerName(job)}</span>
                  </span>
                  <span className="flex items-center gap-1.5">
                    <MapPinIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    <span className="truncate">{job.property?.name || 'N/A'}</span>
                  </span>
                  <span className="flex items-center gap-1.5">
                    <ClockIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    {formatTime(job.scheduled_start_time || '')} – {formatTime(job.scheduled_end_time || '')}
                  </span>
                </div>
              }
              footer={
                <>
                  {(job.status === 'draft' || job.status === 'scheduled') && (
                    <MobileRowAction
                      tone="success"
                      onClick={() => handleJobStatusChange(job.id, 'in_progress')}
                      className={isUpdating ? 'opacity-50 pointer-events-none' : ''}
                    >
                      <PlayIcon className="w-4 h-4" />
                      {isUpdating ? '…' : 'Start'}
                    </MobileRowAction>
                  )}
                  {job.status === 'in_progress' && (
                    <MobileRowAction
                      tone="success"
                      onClick={() => handleJobStatusChange(job.id, 'completed')}
                      className={isUpdating ? 'opacity-50 pointer-events-none' : ''}
                    >
                      <CheckCircleIcon className="w-4 h-4" />
                      {isUpdating ? '…' : 'Complete'}
                    </MobileRowAction>
                  )}
                  <MobileRowAction tone="primary" onClick={() => navigate(`/jobs/details/${job.id}`)}>
                    <EyeIcon className="w-4 h-4" />
                    View
                  </MobileRowAction>
                </>
              }
            />
          )
        })}
      </div>

      <div className="native-desktop-table divide-y divide-gray-100">
        {filteredJobs.map((job) => {
          const isUpdating = updatingJobId === job.id
          return (
            <div
              key={job.id}
              className="flex items-center gap-4 py-3 px-2 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <div className="flex-shrink-0 p-1.5 bg-blue-50 rounded-lg">{getStatusIcon(job.status)}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-900 truncate">{job.title}</span>
                  <span className="text-xs text-gray-400">#{job.job_number}</span>
                </div>
                <div className="flex items-center gap-4 mt-0.5 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <UserIcon className="w-3 h-3" />
                    {getCustomerName(job)}
                  </span>
                  <span className="flex items-center gap-1">
                    <MapPinIcon className="w-3 h-3" />
                    {job.property?.name || 'N/A'}
                  </span>
                  <span className="flex items-center gap-1">
                    <ClockIcon className="w-3 h-3" />
                    {formatTime(job.scheduled_start_time || '')} - {formatTime(job.scheduled_end_time || '')}
                  </span>
                </div>
              </div>
              <div className="flex-shrink-0 flex items-center gap-2">
                <span
                  className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(job.status)}`}
                >
                  {job.status.replace('_', ' ')}
                </span>
                <span
                  className={`inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded ${getPriorityColor(job.priority)}`}
                >
                  {job.priority}
                </span>
              </div>
              <div className="flex-shrink-0 flex items-center gap-1">
                {(job.status === 'draft' || job.status === 'scheduled') && (
                  <button
                    onClick={() => handleJobStatusChange(job.id, 'in_progress')}
                    disabled={isUpdating}
                    className="min-h-[44px] px-3 py-1.5 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    {isUpdating ? '...' : 'Start'}
                  </button>
                )}
                {job.status === 'in_progress' && (
                  <button
                    onClick={() => handleJobStatusChange(job.id, 'completed')}
                    disabled={isUpdating}
                    className="min-h-[44px] px-3 py-1.5 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    {isUpdating ? '...' : 'Complete'}
                  </button>
                )}
                <button
                  onClick={() => navigate(`/jobs/details/${job.id}`)}
                  className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-md transition-colors"
                  title="View Details"
                >
                  <EyeIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          )
        })}
      </div>
    </>
  )

  const renderRoutingView = () => {
    const optimizableCount = routeBuckets.filter(
      (b) => b.kind !== 'unassigned' && b.jobs.length >= 2
    ).length
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div>
            <h3 className="text-sm font-semibold text-gray-700">Route Planning</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Separate routes per assignee. Optimize uses employee last location (punch GPS, else last completed job) when available.
            </p>
          </div>
          <button
            type="button"
            onClick={handleOptimizeAllRoutes}
            disabled={optimizingRoute || optimizableCount === 0}
            className="flex items-center gap-1.5 min-h-[44px] px-3 py-2 text-xs font-medium bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors disabled:opacity-50"
          >
            <NavigationIcon className="w-3.5 h-3.5" />
            {optimizingRoute ? 'Optimizing…' : 'Optimize all'}
          </button>
        </div>

        {filteredJobs.some(
          (j) =>
            j.property &&
            (j.property.latitude == null || j.property.longitude == null) &&
            j.property.address?.trim()
        ) && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded px-2 py-1.5">
            Still missing map coordinates after geocode — check those property addresses, then Optimize again.
          </p>
        )}
        {filteredJobs.some(
          (j) =>
            j.property &&
            (j.property.latitude == null || j.property.longitude == null) &&
            !j.property.address?.trim()
        ) && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded px-2 py-1.5">
            Some properties have no street address, so they cannot be mapped and stay at the end of the route.
          </p>
        )}

        {routeBuckets.length === 0 ? (
          <p className="text-sm text-gray-500 py-6 text-center">No jobs to route for this day.</p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {routeBuckets.map((bucket) => {
              const mapsUrl = mapsUrlByBucket[bucket.key]
              const isBusy = optimizingRoute && optimizingBucketKey === bucket.key
              return (
                <div
                  key={bucket.key}
                  className={`bg-white rounded-lg border p-4 ${
                    bucket.kind === 'unassigned'
                      ? 'border-amber-200'
                      : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2 mb-3 flex-wrap">
                    <div className="min-w-0">
                      <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
                        {bucket.kind === 'crew' ? (
                          <UsersIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
                        ) : (
                          <UserIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
                        )}
                        <span className="truncate">{bucket.label}</span>
                        <span className="text-xs font-medium text-gray-500">
                          ({bucket.jobs.length})
                        </span>
                      </h4>
                      {bucket.kind === 'unassigned' && (
                        <p className="text-[11px] text-amber-700 mt-0.5">
                          Assign these on the Dispatch tab, then optimize.
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {mapsUrl && (
                        <a
                          href={mapsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 px-2.5 py-1 text-xs border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors"
                        >
                          <MapPinIcon className="w-3.5 h-3.5" />
                          Maps
                        </a>
                      )}
                      <button
                        type="button"
                        onClick={() => handleOptimizeBucket(bucket)}
                        disabled={optimizingRoute || bucket.jobs.length < 2}
                        className="flex items-center gap-1 min-h-[44px] px-3 py-2 text-xs font-medium bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors disabled:opacity-50"
                      >
                        <NavigationIcon className="w-3.5 h-3.5" />
                        {isBusy ? 'Optimizing…' : 'Optimize'}
                      </button>
                    </div>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {bucket.jobs.map((job, index) => (
                      <div
                        key={job.id}
                        className="flex items-center gap-3 py-2.5 hover:bg-gray-50 rounded-md px-1 transition-colors"
                      >
                        <div className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-[10px] font-semibold">
                          {job.route_order ?? index + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900 truncate">
                              {job.title}
                            </span>
                            <span className="text-xs text-gray-400">#{job.job_number}</span>
                          </div>
                          <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500 flex-wrap">
                            <span className="flex items-center gap-1 truncate">
                              <MapPinIcon className="w-3 h-3" />
                              {job.property?.name || job.property?.address || 'N/A'}
                            </span>
                            <span className="flex items-center gap-1">
                              <ClockIcon className="w-3 h-3" />
                              {formatTime(job.scheduled_start_time || '')}
                            </span>
                          </div>
                        </div>
                        <span
                          className={`flex-shrink-0 inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full ${getStatusColor(job.status)}`}
                        >
                          {job.status.replace('_', ' ')}
                        </span>
                        <button
                          type="button"
                          onClick={() => navigate(`/jobs/details/${job.id}`)}
                          className="flex-shrink-0 p-1.5 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-md transition-colors"
                          title="View Details"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  const renderAnalyticsView = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="flex items-center gap-3 bg-gray-50 p-3 rounded-lg">
          <div className="p-1.5 bg-blue-100 rounded-lg"><BarChart3Icon className="w-5 h-5 text-blue-600" /></div>
          <div><p className="text-xs text-gray-500">Total</p><p className="text-xl font-bold text-gray-900">{totalJobs}</p></div>
        </div>
        <div className="flex items-center gap-3 bg-gray-50 p-3 rounded-lg">
          <div className="p-1.5 bg-green-100 rounded-lg"><CheckCircleIcon className="w-5 h-5 text-green-600" /></div>
          <div><p className="text-xs text-gray-500">Completed</p><p className="text-xl font-bold text-gray-900">{completedJobs}</p></div>
        </div>
        <div className="flex items-center gap-3 bg-gray-50 p-3 rounded-lg">
          <div className="p-1.5 bg-yellow-100 rounded-lg"><PlayIcon className="w-5 h-5 text-yellow-600" /></div>
          <div><p className="text-xs text-gray-500">In Progress</p><p className="text-xl font-bold text-gray-900">{inProgressJobs}</p></div>
        </div>
        <div className="flex items-center gap-3 bg-gray-50 p-3 rounded-lg">
          <div className="p-1.5 bg-purple-100 rounded-lg"><ClockIcon className="w-5 h-5 text-purple-600" /></div>
          <div><p className="text-xs text-gray-500">Completion</p><p className="text-xl font-bold text-gray-900">{completionRate}%</p></div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="flex items-center justify-between bg-gray-50 px-4 py-2.5 rounded-lg">
          <div className="flex items-center gap-2"><div className="w-2.5 h-2.5 bg-blue-500 rounded-full"></div><span className="text-sm text-gray-600">Scheduled</span></div>
          <span className="text-sm font-bold text-gray-900">{scheduledJobs}</span>
        </div>
        <div className="flex items-center justify-between bg-gray-50 px-4 py-2.5 rounded-lg">
          <div className="flex items-center gap-2"><div className="w-2.5 h-2.5 bg-yellow-500 rounded-full"></div><span className="text-sm text-gray-600">In Progress</span></div>
          <span className="text-sm font-bold text-gray-900">{inProgressJobs}</span>
        </div>
        <div className="flex items-center justify-between bg-gray-50 px-4 py-2.5 rounded-lg">
          <div className="flex items-center gap-2"><div className="w-2.5 h-2.5 bg-green-500 rounded-full"></div><span className="text-sm text-gray-600">Completed</span></div>
          <span className="text-sm font-bold text-gray-900">{completedJobs}</span>
        </div>
      </div>
    </div>
  )

  const renderContent = () => {
    switch (viewMode) {
      case 'schedule': return renderScheduleView()
      case 'dispatch': return renderDispatchView()
      case 'progress': return renderProgressView()
      case 'routing': return renderRoutingView()
      case 'analytics': return renderAnalyticsView()
      default: return renderScheduleView()
    }
  }

  const tabBtn = (active: boolean) =>
    `flex items-center gap-2 px-4 sm:px-6 min-h-[48px] text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
      active
        ? 'border-blue-500 text-blue-600'
        : 'border-transparent text-gray-500 hover:text-gray-700'
    }`

  const divisionChip = (selected: boolean, accent: 'blue' | 'purple' = 'blue') =>
    `whitespace-nowrap min-h-[44px] px-3.5 py-2 text-xs font-medium rounded-full border ${
      selected
        ? accent === 'purple'
          ? 'bg-purple-600 text-white border-purple-600'
          : 'bg-blue-600 text-white border-blue-600'
        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
    }`

  return (
    <MobilePage>
      <MobilePageHeader
        title="Daily Dispatch"
        subtitle={dateFormatted}
        onBack={() => navigate('/job-calendar')}
        backLabel="Back to Calendar"
        actions={
          <div className="flex items-center gap-1.5 sm:gap-2">
            <button
              type="button"
              onClick={() => navigateDay(-1)}
              className={mobile.ghostBtn}
              aria-label="Previous day"
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={() => navigate(`/dispatch/${formatDateInputValue(new Date())}`)}
              className={`${mobile.secondaryBtn} min-h-[44px] px-3 text-sm`}
            >
              Today
            </button>
            <button
              type="button"
              onClick={() => navigateDay(1)}
              className={mobile.ghostBtn}
              aria-label="Next day"
            >
              <ChevronRightIcon className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={() => navigate('/jobs/create')}
              className={`${mobile.primaryBtn} text-sm`}
            >
              <span className="sm:hidden">New</span>
              <span className="hidden sm:inline">+ New Job</span>
            </button>
          </div>
        }
      />

      <MobileStatStrip
        className="md:hidden"
        stats={[
          { key: 'total', label: 'Total', value: totalJobs },
          { key: 'scheduled', label: 'Scheduled', value: scheduledJobs, tone: 'amber' },
          { key: 'progress', label: 'In Progress', value: inProgressJobs, tone: 'blue' },
          { key: 'done', label: 'Completed', value: completedJobs, tone: 'green' },
        ]}
      />

      <div className="hidden md:grid grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 text-center">
          <p className="text-2xl font-bold text-gray-900">{totalJobs}</p>
          <p className="text-xs text-gray-500 mt-1">Total Jobs</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 text-center">
          <p className="text-2xl font-bold text-yellow-600">{scheduledJobs}</p>
          <p className="text-xs text-gray-500 mt-1">Scheduled</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 text-center">
          <p className="text-2xl font-bold text-blue-600">{inProgressJobs}</p>
          <p className="text-xs text-gray-500 mt-1">In Progress</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 text-center">
          <p className="text-2xl font-bold text-green-600">{completedJobs}</p>
          <p className="text-xs text-gray-500 mt-1">Completed</p>
        </div>
      </div>

      <div className={`${mobile.surface} overflow-hidden`}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 sm:p-4 border-b border-gray-200">
          <p className="text-sm text-gray-500">
            {totalJobs} job{totalJobs !== 1 ? 's' : ''} scheduled
          </p>
          <MobileSelect
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="sm:max-w-[12rem]"
          >
            <option value="all">All Statuses</option>
            <option value="scheduled">Scheduled</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="pending_review">Pending Review</option>
          </MobileSelect>
        </div>

        {/* Work pool / division chips */}
        <div className="flex items-center gap-2 px-3 sm:px-4 py-3 border-b border-gray-200 overflow-x-auto bg-gray-50">
          <button
            type="button"
            onClick={() => {
              setDispatchScope('division')
              setCurrentDivision(null)
            }}
            className={divisionChip(dispatchScope === 'division' && !currentDivision)}
          >
            All
            {countUnassigned(jobs) > 0 && ` · ${countUnassigned(jobs)} unassigned`}
          </button>
          {divisions.map((division) => {
            const divisionJobs = jobs.filter((job) => matchesDivision(job, division))
            const divisionUnassigned = countUnassigned(divisionJobs)
            const selected =
              dispatchScope === 'division' && currentDivision?.id === division.id
            return (
              <button
                key={division.id}
                type="button"
                onClick={() => {
                  setDispatchScope('division')
                  setCurrentDivision(division)
                }}
                className={divisionChip(!!selected)}
              >
                {division.name}
                {divisionUnassigned > 0 && ` · ${divisionUnassigned} unassigned`}
              </button>
            )
          })}
          <button
            type="button"
            onClick={() => setDispatchScope('subs')}
            className={divisionChip(dispatchScope === 'subs', 'purple')}
          >
            Subs
            {countUnassigned(subcontractorJobs) > 0 &&
              ` · ${countUnassigned(subcontractorJobs)} unassigned`}
          </button>
        </div>

        {/* View tabs */}
        <div className="flex border-b border-gray-200 overflow-x-auto">
          <button
            type="button"
            onClick={() => setViewMode('schedule')}
            className={tabBtn(viewMode === 'schedule')}
          >
            <CalendarIcon className="w-4 h-4" />
            Schedule
          </button>
          <button
            type="button"
            onClick={() => setViewMode('dispatch')}
            className={tabBtn(viewMode === 'dispatch')}
          >
            <UsersIcon className="w-4 h-4" />
            Dispatch
            {unassignedCount > 0 && (
              <span className="ml-0.5 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold">
                {unassignedCount}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => setViewMode('progress')}
            className={tabBtn(viewMode === 'progress')}
          >
            <PlayIcon className="w-4 h-4" />
            Job Progress
          </button>
          <button
            type="button"
            onClick={() => setViewMode('routing')}
            className={tabBtn(viewMode === 'routing')}
          >
            <NavigationIcon className="w-4 h-4" />
            Route Planning
          </button>
          <button
            type="button"
            onClick={() => setViewMode('analytics')}
            className={tabBtn(viewMode === 'analytics')}
          >
            <BarChart3Icon className="w-4 h-4" />
            Analytics
          </button>
        </div>

        <div className="p-3 sm:p-4 lg:p-6">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2Icon className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : filteredJobs.length === 0 ? (
            <MobileEmptyState
              icon={<CalendarIcon className="w-12 h-12" />}
              title="No jobs scheduled"
              description={
                statusFilter !== 'all'
                  ? 'No jobs found with the selected status filter.'
                  : `No jobs are scheduled for ${dateFormatted}.`
              }
            />
          ) : (
            renderContent()
          )}
        </div>
      </div>

      <CompletionExceptionModal
        open={!!exceptionJobId}
        gateMessage={exceptionGateMessage}
        busy={!!updatingJobId}
        onCancel={() => {
          setExceptionJobId(null)
          setExceptionGateMessage(null)
        }}
        onConfirm={async (reason) => {
          if (!exceptionJobId) return
          const jobId = exceptionJobId
          setExceptionJobId(null)
          setExceptionGateMessage(null)
          await applyJobStatusChange(jobId, 'completed', { reason })
        }}
      />
    </MobilePage>
  )
}
